import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI, useTRPCMutation } from '../../../trpc/trpc';

export interface UseFollowArtistOptions {
  onSuccess?: (artistId: string) => void;
  onError?: (error: Error, artistId: string) => void;
}

export const useFollowArtist = (options: UseFollowArtistOptions = {}) => {
  const { onSuccess, onError } = options;
  const trpcMusicAPI = useTRPCAPI();
  const queryClient = useQueryClient();

  // Use refs to always have access to latest callbacks without recreating mutation
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  // Track current artistId for callbacks
  const currentArtistIdRef = useRef<string | null>(null);

  // Follow and unfollow mutations
  const followMutation = useTRPCMutation(trpcMusicAPI.artists.follow.mutationOptions());
  const unfollowMutation = useTRPCMutation(trpcMusicAPI.artists.unfollow.mutationOptions());

  const followArtist = useCallback(
    (artistId: string) => {
      currentArtistIdRef.current = artistId;
      followMutation.mutate(
        { artistId },
        {
          onSuccess: () => {
            // Invalidate specific artist-related queries to refresh data (async, fire-and-forget)
            Promise.all([
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.artists.listFollowed.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.artists.isFollowing.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.artists.details.queryKey(),
              }),
            ]).catch(console.error);
            onSuccessRef.current?.(artistId);
          },
          onError: (error) => {
            const errorObj = error instanceof Error ? error : new Error(error.message);
            onErrorRef.current?.(errorObj, artistId);
          },
        }
      );
    },
    [
      followMutation,
      queryClient,
      trpcMusicAPI.artists.listFollowed,
      trpcMusicAPI.artists.isFollowing,
      trpcMusicAPI.artists.details,
    ]
  );

  const unfollowArtist = useCallback(
    (artistId: string) => {
      currentArtistIdRef.current = artistId;
      unfollowMutation.mutate(
        { artistId },
        {
          onSuccess: () => {
            // Invalidate specific artist-related queries to refresh data (async, fire-and-forget)
            Promise.all([
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.artists.listFollowed.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.artists.isFollowing.queryKey(),
              }),
              queryClient.invalidateQueries({
                queryKey: trpcMusicAPI.artists.details.queryKey(),
              }),
            ]).catch(console.error);
            onSuccessRef.current?.(artistId);
          },
          onError: (error) => {
            const errorObj = error instanceof Error ? error : new Error(error.message);
            onErrorRef.current?.(errorObj, artistId);
          },
        }
      );
    },
    [
      unfollowMutation,
      queryClient,
      trpcMusicAPI.artists.listFollowed,
      trpcMusicAPI.artists.isFollowing,
      trpcMusicAPI.artists.details,
    ]
  );

  return {
    followArtist,
    unfollowArtist,
    isFollowPending: followMutation.isPending,
    isUnfollowPending: unfollowMutation.isPending,
    isPending: followMutation.isPending || unfollowMutation.isPending,
  };
};
